/**
 * MazeStriker — self-contained 3D voxel-maze game runtime, hosted in a react-native-webview (native) /
 * srcDoc iframe (web). Ported from the design-handoff prototype (specs/mazestriker/design_handoff/
 * "Zporti Maze Adventure.dc.html") — the prototype's DCLogic reactive harness is dropped in favour of
 * plain imperative DOM; only the gameplay is kept, exactly per the handoff constants.
 *
 * Why a string module: the whole game (three.js scene + RAF loop + input + WebAudio sfx) ships with the
 * app as one HTML document with ZERO network dependencies — three.js r128 and the Bungee/Chakra Petch
 * fonts are inlined (gzip+base64 / woff2+base64) so it always works offline (spec AC-15).
 *
 * Bridge protocol (window.ReactNativeWebView.postMessage, window.parent fallback; JSON):
 *   web → RN:  { type:'ready' }
 *            | { type:'run-end', outcome:'win'|'dead'|'timeout', levelsCleared, coinsCollected, score, best }
 *            | { type:'load-error' }
 *   RN → web:  none (host ref supports reload only)
 *
 * The economy is server-authoritative: `levelsCleared` + `coinsCollected` are the only fields the server
 * banks from (both clamped + daily-capped); `score`/`best` are advisory (best-score stat only). A spoofed
 * run-end can never out-earn an honest player. See specs/mazestriker/spec.md + server/src/constants/mazeStriker.
 *
 * Copy guard: no "bet"/prediction wording, no real team/player/tournament/World-Cup names, no "unlimited".
 */

import { THREE_R128_GZIP_BASE64 } from './mazeStrikerThree';
import { MAZE_STRIKER_FONT_FACE_CSS } from './mazeStrikerFonts';

export function buildMazeStrikerGameHtml(): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
<title>MazeStriker</title>
<style>
${MAZE_STRIKER_FONT_FACE_CSS}
*{margin:0;padding:0;box-sizing:border-box;-webkit-tap-highlight-color:transparent}
/* touch-action:none must NOT live on html/body — it blocks touch scrolling of the title screen on
   mobile. It goes on the game surfaces only (#maze-mount, .dpad, #btn-kick). */
html,body{width:100%;height:100%;overflow:hidden;background:#0b131a;color:#eef5ea;
  font-family:'Chakra Petch',system-ui,sans-serif;user-select:none;-webkit-user-select:none}
#root{position:relative;width:100vw;height:100vh;overflow:hidden}
#maze-mount{position:absolute;inset:0;background:#0b131a;touch-action:none}
#screen-title>div{touch-action:pan-y;-webkit-overflow-scrolling:touch}
#maze-mount canvas{display:block}
.screen{position:absolute;inset:0}
.hidden{display:none!important}
.chip{background:#0e1a24cc;border:2px solid #23414f;border-radius:4px;white-space:nowrap}
.bungee{font-family:'Bungee',sans-serif}
.cta{font-family:'Bungee',sans-serif;border-radius:6px;cursor:pointer;line-height:1.2;white-space:nowrap}
@keyframes zpop{0%{opacity:0;transform:translateY(14px) scale(.8)}18%{opacity:1;transform:translateY(0) scale(1.06)}70%{opacity:1;transform:translateY(-16px) scale(1)}100%{opacity:0;transform:translateY(-40px) scale(.95)}}
@keyframes zglow{0%,100%{box-shadow:0 0 0 0 rgba(198,242,78,.5)}50%{box-shadow:0 0 0 10px rgba(198,242,78,0)}}
</style>
</head>
<body>
<div id="root">
  <div id="maze-mount"></div>

  <!-- ============ PLAY HUD ============ -->
  <div id="hud" class="screen hidden">
    <div style="position:absolute;top:0;left:0;right:0;padding:14px 16px;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;pointer-events:none">
      <div style="display:flex;flex-direction:column;gap:8px">
        <div style="display:flex;align-items:center;gap:10px">
          <div class="chip" style="padding:6px 12px;display:flex;align-items:baseline;gap:8px">
            <span class="bungee" style="font-size:13px;letter-spacing:1px;line-height:1.2;color:#8fb0a4">SCORE</span>
            <span id="hud-score" class="bungee" style="font-size:24px;line-height:1.1;color:#c6f24e">0</span>
          </div>
          <div id="hud-best" class="chip" style="padding:6px 12px;font-size:14px;font-weight:700;line-height:1.2;color:#8fb0a4">BEST 0</div>
        </div>
        <div style="display:flex;align-items:center;gap:8px">
          <div id="hud-time" class="chip bungee" style="padding:6px 12px;font-size:18px;line-height:1.2;color:#eef5ea">3:30</div>
          <div id="hud-hearts" class="chip" style="display:flex;gap:5px;padding:8px 10px"></div>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <div class="chip" style="padding:6px 12px;font-size:14px;font-weight:700;line-height:1.2;letter-spacing:.5px">LEVEL <span id="hud-level">1</span><span style="color:#5f7d8c">/<span id="hud-total">3</span></span></div>
          <div id="hud-obj" class="chip" style="padding:6px 12px;font-size:14px;font-weight:700;line-height:1.2;letter-spacing:.5px;color:#8a56c8;border-color:#8a56c8">0 MONSTERS LEFT</div>
        </div>
        <div id="hud-balls-wrap" class="chip" style="display:flex;gap:5px;align-items:center;padding:7px 11px;width:fit-content">
          <span style="font-size:13px;font-weight:700;line-height:1.2;color:#8fb0a4;letter-spacing:1px">BALLS</span>
          <span id="hud-balls" style="display:flex;gap:5px"></span>
        </div>
      </div>
      <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
        <canvas id="maze-minimap" width="180" height="180" style="width:150px;height:150px;background:#0e1a24cc;border:2px solid #23414f;border-radius:4px;image-rendering:pixelated"></canvas>
        <div style="display:flex;gap:6px;pointer-events:auto">
          <div id="btn-mute" class="chip" style="padding:6px 10px;font-size:12px;font-weight:700;letter-spacing:.5px;line-height:1.2;cursor:pointer;color:#c6f24e">SOUND ON</div>
          <div id="btn-zin" class="chip" style="padding:6px 11px;font-size:13px;font-weight:700;line-height:1.2;cursor:pointer;color:#8fb0a4">+</div>
          <div id="btn-zout" class="chip" style="padding:6px 11px;font-size:13px;font-weight:700;line-height:1.2;cursor:pointer;color:#8fb0a4">–</div>
        </div>
        <div id="hud-popups" style="display:flex;flex-direction:column;align-items:center;gap:4px;min-height:120px;justify-content:flex-start"></div>
      </div>
    </div>

    <div id="dpad-wrap" style="position:absolute;bottom:20px;left:20px;display:grid;grid-template-columns:repeat(3,56px);grid-template-rows:repeat(2,56px);gap:8px">
      <div class="dpad" data-dir="up" style="grid-column:2;grid-row:1">▲</div>
      <div class="dpad" data-dir="left" style="grid-column:1;grid-row:2">◀</div>
      <div class="dpad" data-dir="down" style="grid-column:2;grid-row:2">▼</div>
      <div class="dpad" data-dir="right" style="grid-column:3;grid-row:2">▶</div>
    </div>

    <div id="hud-keys-hint" style="position:absolute;bottom:92px;left:50%;transform:translateX(-50%);background:#0e1a24d9;border:2px solid #2d5566;border-radius:6px;padding:8px 14px;font-size:13px;line-height:1.4;white-space:nowrap;color:#8fb0a4;text-align:center;pointer-events:none">WASD / arrows to move · SPACE or X to kick · ricochets build combos · scroll to zoom · M mutes</div>

    <div style="position:absolute;bottom:20px;right:20px;display:flex;align-items:flex-end;gap:12px">
      <div id="btn-kick" style="width:104px;height:104px;border-radius:50%;background:#c6f24e;border:4px solid #97c22f;display:flex;align-items:center;justify-content:center;font-family:'Bungee',sans-serif;font-size:19px;color:#132018;animation:zglow 2s infinite;cursor:pointer">KICK</div>
    </div>
  </div>

  <!-- ============ TITLE ============ -->
  <div id="screen-title" class="screen">
    <div style="position:absolute;inset:0;background:radial-gradient(120% 90% at 50% 0%,#16323d 0%,#0b131a 70%);display:flex;flex-direction:column;align-items:center;justify-content:flex-start;gap:14px;padding:20px 24px;overflow:auto">
      <div style="display:flex;flex-direction:column;align-items:center;gap:6px;margin-top:auto">
        <div class="bungee" style="font-size:13px;letter-spacing:5px;line-height:1.4;color:#c6f24e">ZPORTI PRESENTS</div>
        <div class="bungee" style="font-size:clamp(34px,7vw,58px);line-height:1;text-align:center;letter-spacing:1px">MAZE<span style="color:#ff7a2f">STRIKER</span></div>
        <div style="font-size:15px;line-height:1.45;color:#8fb0a4;text-align:center;max-width:560px;text-wrap:pretty">Kick the ball through the blocky labyrinth. Ricochet it off walls to smash every monster, wake the boss, then find the gate before the clock runs out.</div>
      </div>
      <div style="display:flex;flex-direction:column;gap:8px;align-items:center">
        <div class="bungee" style="font-size:14px;letter-spacing:3px;line-height:1.5;color:#8fb0a4">PICK YOUR PLAYER</div>
        <div id="avatar-grid" style="display:flex;flex-wrap:wrap;gap:8px;justify-content:center;max-width:820px"></div>
      </div>
      <div style="display:flex;flex-direction:column;align-items:center;gap:8px;margin-bottom:auto">
        <div id="btn-start" class="cta bungee" style="background:#c6f24e;color:#132018;font-size:21px;padding:13px 40px;border-bottom:5px solid #8aae2b">START RUN</div>
        <div style="font-size:14px;line-height:1.3;color:#5f7d8c">Best score <span id="title-best">0</span></div>
      </div>
    </div>
  </div>

  <!-- ============ INTRO ============ -->
  <div id="screen-intro" class="screen hidden">
    <div id="intro-tap" style="position:absolute;inset:0;background:#0b131ae6;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;padding:32px;text-align:center;cursor:pointer">
      <div class="bungee" style="font-size:14px;letter-spacing:4px;line-height:1.4;color:#8fb0a4">LEVEL <span id="intro-level">1</span> OF <span id="intro-total">3</span></div>
      <div id="intro-name" class="bungee" style="font-size:clamp(28px,5vw,42px);line-height:1.15;color:#c6f24e">THE COURTYARD</div>
      <div id="intro-brief" style="font-size:17px;color:#c9d8cd;max-width:520px;line-height:1.6;text-wrap:pretty"></div>
      <div class="cta bungee" style="background:#c6f24e;color:#132018;font-size:19px;padding:14px 38px;border-bottom:5px solid #8aae2b">ENTER THE MAZE</div>
    </div>
  </div>

  <!-- ============ COMPLETE ============ -->
  <div id="screen-complete" class="screen hidden">
    <div id="complete-tap" style="position:absolute;inset:0;background:#0b131ae6;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:32px;text-align:center;cursor:pointer">
      <div class="bungee" style="font-size:clamp(28px,5vw,42px);line-height:1.15;color:#c6f24e">LEVEL CLEARED</div>
      <div id="complete-line" style="font-size:18px;line-height:1.5;color:#c9d8cd;max-width:460px"></div>
      <div class="bungee" style="font-size:28px;line-height:1.2">SCORE <span id="complete-score">0</span></div>
      <div class="cta bungee" style="background:#ff7a2f;color:#1b0f06;font-size:19px;padding:14px 38px;border-bottom:5px solid #c25a1c">NEXT LEVEL</div>
    </div>
  </div>

  <!-- ============ OVER ============ -->
  <div id="screen-over" class="screen hidden">
    <div id="over-tap" style="position:absolute;inset:0;background:#0b131aee;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:32px;text-align:center;cursor:pointer">
      <div id="over-title" class="bungee" style="font-size:clamp(28px,5vw,42px);line-height:1.15;color:#c6f24e">RUN COMPLETE</div>
      <div id="over-line" style="font-size:18px;line-height:1.5;color:#c9d8cd;max-width:480px;text-wrap:pretty"></div>
      <div class="bungee" style="font-size:32px;line-height:1.2;color:#c6f24e">SCORE <span id="over-score">0</span></div>
      <div style="font-size:15px;line-height:1.3;color:#5f7d8c">BEST <span id="over-best">0</span></div>
      <div class="cta bungee" style="background:#c6f24e;color:#132018;font-size:19px;padding:14px 38px;border-bottom:5px solid #8aae2b">PLAY AGAIN</div>
    </div>
  </div>
</div>

<style>
.dpad{background:#0e1a24d9;border:2px solid #2d5566;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:22px;color:#c6f24e;font-weight:700;cursor:pointer;touch-action:none}
#btn-kick{touch-action:none}
/* The HUD doubles as the drag-to-move surface during play — no browser panning on it. */
#hud{touch-action:none}
/* The hint sits front-and-center — it fades out a few seconds into each level (JS adds .gone). */
#hud-keys-hint{transition:opacity .6s ease}
#hud-keys-hint.gone{opacity:0}
/* Touch devices steer by dragging the maze directly — the arrow dpad is desktop-only, and the
   KICK button shrinks so it covers less of the maze. */
@media (pointer: coarse) {
  #dpad-wrap{display:none !important}
  #btn-kick{width:72px !important;height:72px !important;font-size:14px !important;border-width:3px !important}
  #hud-keys-hint{bottom:110px}
}
/* Landscape phone (short viewport): keep the hint near the free bottom-center strip. */
@media (pointer: coarse) and (max-height: 520px) {
  #hud-keys-hint{bottom:14px}
}
/* Compact HUD for phones — BOTH orientations: narrow (portrait) or short (landscape) viewport. */
@media (max-width: 480px), (max-height: 520px) {
  #hud>div:first-child { padding: 6px 8px !important; gap: 6px !important; }
  #hud>div:first-child>div { gap: 5px !important; }
  .chip { padding: 3px 7px !important; font-size: 10px !important; }
  #hud-score { font-size: 14px !important; }
  #hud-best { font-size: 10px !important; }
  #hud-time { font-size: 12px !important; }
  #hud-level, #hud-obj, #hud-balls-wrap { font-size: 10px !important; }
  #hud-hearts { padding: 4px 7px !important; }
  #maze-minimap { display: none !important; }
  #btn-mute, #btn-zin, #btn-zout { padding: 3px 6px !important; font-size: 9px !important; }
  #hud-keys-hint { font-size: 11px !important; padding: 5px 10px !important; }
  #hud-popups { min-height: 0 !important; }
}
/* Portrait-narrow only: stack the two topbar groups so chips never collide with the right column. */
@media (max-width: 420px) {
  #hud>div:first-child { flex-direction: column !important; align-items: flex-start !important; }
}
@media (max-width: 420px), (max-height: 520px) {
  #screen-title>div { padding: 16px 12px !important; gap: 10px !important; }
  #avatar-grid { gap: 6px !important; }
  #avatar-grid>div { width: 80px !important; padding: 6px 5px !important; }
  #avatar-grid>div>div:first-child { width: 60px !important; height: 60px !important; }
  #avatar-grid .av-name { font-size: 10px !important; }
  #btn-start { font-size: 16px !important; padding: 10px 28px !important; border-bottom-width: 3px !important; }
  #screen-intro>div, #screen-complete>div, #screen-over>div { padding: 24px !important; gap: 12px !important; }
  #screen-intro>div .bungee, #screen-complete>div .bungee, #screen-over>div .bungee { font-size: 20px !important; }
  #screen-intro>div>div:not(.bungee), #screen-complete>div>div:not(.bungee), #screen-over>div>div:not(.bungee) { font-size: 14px !important; }
  #screen-intro>div .cta, #screen-complete>div .cta, #screen-over>div .cta { font-size: 16px !important; padding: 10px 28px !important; border-bottom-width: 3px !important; }
}
</style>

<script>
window.__errs = [];
window.onerror = function(m, s, l, c){ window.__errs.push(String(m) + ' @' + l + ':' + c); };
// Bridge: native WebView first, srcDoc-iframe parent as the web fallback (same contract as Striker Run).
window.__post = function(m){
  var s = JSON.stringify(m);
  try { if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) { window.ReactNativeWebView.postMessage(s); return; } } catch (e) {}
  try { if (window.parent && window.parent !== window) window.parent.postMessage(s, '*'); } catch (e) {}
};
</script>

<!-- three.js r128, gzip+base64. Decoded at boot via DecompressionStream; any failure posts load-error. -->
<script id="__three_gz" type="text/plain">${THREE_R128_GZIP_BASE64}</script>
<script>
(function(){
  function inject(src){ var s = document.createElement('script'); s.textContent = src; document.head.appendChild(s); }
  try {
    var b64 = document.getElementById('__three_gz').textContent.trim();
    var bin = atob(b64), bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    if (typeof DecompressionStream !== 'function') { window.__post({ type: 'load-error' }); return; }
    new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text()
      .then(inject)
      .catch(function(){ window.__post({ type: 'load-error' }); });
  } catch (e) { window.__errs.push('three-decode: ' + String(e)); window.__post({ type: 'load-error' }); }
})();
</script>

<script>
(function(){
  'use strict';
  var post = window.__post;
  var el = function(id){ return document.getElementById(id); };

  var AVATARS = [
    { name: 'Moykan', ball: '#f4f4ef', patch: '#1a1a1a', hair: '#1f8a4c', jersey: '#1f8a4c', trim: '#f4f4ef' },
    { name: 'Blue Hair', ball: '#3f7fd4', patch: '#6b4a2a', hair: '#6b4a2a', jersey: '#24405f', trim: '#8fc4f0' },
    { name: 'White Gold', ball: '#f2e6c8', patch: '#1a1a1a', hair: '#f2c23a', jersey: '#2a2a2e', trim: '#f2c23a' },
    { name: 'Red Ace', ball: '#f4f4ef', patch: '#c33232', hair: '#2a2a2e', jersey: '#c33232', trim: '#f4d24a' },
    { name: 'Violet', ball: '#e8dcf4', patch: '#5b3a8e', hair: '#5b3a8e', jersey: '#7a4fc0', trim: '#e8dcf4' },
    { name: 'Teal Keeper', ball: '#f4f4ef', patch: '#12706b', hair: '#f26b2f', jersey: '#12706b', trim: '#f26b2f' }
  ];
  var LEVEL_NAMES = ['THE COURTYARD', 'STONE CORRIDORS', 'THE DEEP PITCH', 'IRON LABYRINTH', 'FINAL GROUNDS'];
  var LEVEL_BRIEFS = [
    'Smash every monster with a kicked ball, then wake the boss. The gate only opens once nothing is left standing.',
    'Tighter walls, faster chasers. Ricochet off corners to hit two monsters with one kick.',
    'Spitters guard the junctions and the boss hits harder. Grab clocks — the maze is big.',
    'Dead ends everywhere. Keep a spare ball for emergencies.',
    'Everything at once. Survive it and the run is yours.'
  ];
  var SIZES = { compact: [17, 21, 25, 29, 33], large: [25, 29, 33, 37, 41], huge: [33, 39, 45, 51, 57] };

  function MazeGame(props){
    this.props = props;
    this.state = {
      screen: 'title', avatarIndex: 0, score: 0, best: 0, level: 1,
      lives: props.maxLives, timeLeft: props.startSeconds, monstersLeft: 0, bossAwake: false, bossHp: 0,
      heldBalls: 2, won: false, deathCause: '', muted: false
    };
    this.run = { coinsCollected: 0, levelsCleared: 0 };
    this.keys = {};
    this.touch = { x: 0, z: 0 };
    this.CS = 4;
    this.zoom = 1;
    this.ready = false;
    this.reported = false;
  }

  MazeGame.prototype.get_levelTotal = function(){ return Math.max(1, this.props.levelCount); };
  MazeGame.prototype.get_maxLives = function(){ return Math.max(1, this.props.maxLives); };
  MazeGame.prototype.get_startSeconds = function(){ return Math.max(20, this.props.startSeconds); };
  MazeGame.prototype.get_diff = function(){
    var d = this.props.difficulty || 'normal';
    return d === 'easy' ? 0.78 : d === 'hard' ? 1.3 : 1;
  };

  /* -------- lightweight state + DOM render (replaces DCLogic) -------- */
  MazeGame.prototype.setState = function(patch, cb){
    var next = typeof patch === 'function' ? patch(this.state) : patch;
    for (var k in next) this.state[k] = next[k];
    this.renderHud();
    if (cb) cb();
  };

  MazeGame.prototype.mountDom = function(){
    var self = this;
    // avatar tiles
    var grid = el('avatar-grid');
    grid.innerHTML = '';
    this.avatarEls = [];
    AVATARS.forEach(function(a, i){
      var tile = document.createElement('div');
      tile.style.cssText = 'width:104px;padding:9px 7px;background:#0e1a24;border:3px solid #23414f;border-radius:8px;display:flex;flex-direction:column;align-items:center;gap:7px;cursor:pointer';
      tile.innerHTML =
        '<div style="width:74px;height:74px;position:relative">' +
          '<div style="position:absolute;left:9px;top:16px;width:56px;height:56px;border-radius:50%;background:' + a.ball + '"></div>' +
          '<div style="position:absolute;left:30px;top:34px;width:15px;height:15px;background:' + a.patch + ';clip-path:polygon(50% 0,100% 38%,80% 100%,20% 100%,0 38%)"></div>' +
          '<div style="position:absolute;left:15px;top:51px;width:12px;height:12px;background:' + a.patch + ';clip-path:polygon(50% 0,100% 38%,80% 100%,20% 100%,0 38%)"></div>' +
          '<div style="position:absolute;left:47px;top:51px;width:12px;height:12px;background:' + a.patch + ';clip-path:polygon(50% 0,100% 38%,80% 100%,20% 100%,0 38%)"></div>' +
          '<div style="position:absolute;left:12px;top:6px;width:50px;height:16px;border-radius:3px;background:' + a.hair + '"></div>' +
          '<div style="position:absolute;left:21px;bottom:0;width:32px;height:13px;border-radius:2px;background:' + a.jersey + ';border-bottom:4px solid ' + a.trim + '"></div>' +
        '</div>' +
        '<div class="av-name" style="font-size:12px;font-weight:700;letter-spacing:.4px;line-height:1.25;text-align:center;white-space:nowrap;padding:3px 7px;border-radius:4px;background:#16283200;color:#8fb0a4">' + a.name + '</div>';
      tile.addEventListener('click', function(){ self.pickAvatar(i); });
      grid.appendChild(tile);
      self.avatarEls.push(tile);
    });
    this.renderAvatars();

    // buttons
    el('btn-start').addEventListener('click', function(){ self.resumeAudio(); self.start(); });
    el('intro-tap').addEventListener('click', function(){ self.beginLevel(); });
    el('complete-tap').addEventListener('click', function(){ self.nextLevel(); });
    el('over-tap').addEventListener('click', function(){ self.restart(); });
    el('btn-mute').addEventListener('click', function(){ self.setState(function(s){ return { muted: !s.muted }; }); });
    el('btn-zin').addEventListener('click', function(){ self.zoom = Math.max(0.7, self.zoom - 0.2); });
    el('btn-zout').addEventListener('click', function(){ self.zoom = Math.min(2.2, self.zoom + 0.2); });
    el('btn-kick').addEventListener('pointerdown', function(e){ e.preventDefault(); self.resumeAudio(); self.kick(); });

    var dpads = document.querySelectorAll('.dpad');
    var vec = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
    Array.prototype.forEach.call(dpads, function(pad){
      var v = vec[pad.getAttribute('data-dir')];
      var clear = function(){ self.touch.x = 0; self.touch.z = 0; };
      pad.addEventListener('pointerdown', function(e){
        e.preventDefault();
        // Capture so pointerup lands on the pad even when the finger drifts off it mid-hold.
        try { pad.setPointerCapture(e.pointerId); } catch (err) {}
        self.touch.x = v[0]; self.touch.z = v[1];
      });
      pad.addEventListener('pointerup', clear);
      pad.addEventListener('pointercancel', clear);
      pad.addEventListener('pointerleave', clear);
    });

    // Touch input on the play area. One finger: drag-to-steer — the vector from the touch origin
    // to the finger drives touch.x/z (same held-vector contract as the dpad). Two fingers: pinch
    // zoom — spread = zoom in, pinch = zoom out, mapping onto the same 0.7–2.2 camera multiplier
    // the +/− buttons and wheel use (updateCamera lerps, so pinch animates smoothly). Steering
    // stops while pinching; the finger that remains afterwards becomes a fresh steer origin.
    // Touch devices hide the dpad entirely (pointer:coarse CSS) so this IS the movement control
    // there; with a mouse it's a bonus alongside the arrows. Buttons opt out so KICK/mute/zoom
    // keep their own handlers.
    var isCoarse = false;
    try { isCoarse = window.matchMedia('(pointer: coarse)').matches; } catch (e) {}
    if (isCoarse) el('hud-keys-hint').textContent = 'Drag to move \\u00b7 pinch to zoom \\u00b7 KICK to shoot';
    var hudEl = el('hud');
    var pts = {}, ptCount = 0;                    // active bare-area pointers: id -> {x,y}
    var steerId = null, steerOx = 0, steerOy = 0; // single-finger steer origin
    var pinchDist = 0, pinchZoom = 1;             // pinch baseline (distance at 2-finger start)
    var DRAG_DEAD_PX = 12;
    var stopSteer = function(){ steerId = null; self.touch.x = 0; self.touch.z = 0; };
    var pinchPair = function(){
      var ids = Object.keys(pts);
      return ids.length >= 2 ? [pts[ids[0]], pts[ids[1]]] : null;
    };
    var rebasePinch = function(){
      var pair = pinchPair();
      if (!pair) return;
      pinchDist = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y) || 1;
      pinchZoom = self.zoom;
    };
    hudEl.addEventListener('pointerdown', function(e){
      if (e.target !== hudEl) return; // only bare play-area touches; buttons keep their own handlers
      if (self.state.screen !== 'play') return;
      e.preventDefault();
      self.resumeAudio();
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      ptCount++;
      try { hudEl.setPointerCapture(e.pointerId); } catch (err) {}
      if (ptCount === 1) {
        steerId = e.pointerId; steerOx = e.clientX; steerOy = e.clientY;
      } else {
        stopSteer();
        rebasePinch();
      }
    });
    hudEl.addEventListener('pointermove', function(e){
      var p = pts[e.pointerId];
      if (!p) return;
      p.x = e.clientX; p.y = e.clientY;
      if (ptCount >= 2) {
        var pair = pinchPair();
        var d = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y) || 1;
        // Fingers apart = camera closer (zoom is a distance multiplier, smaller = nearer).
        self.zoom = Math.min(2.2, Math.max(0.7, pinchZoom * (pinchDist / d)));
      } else if (e.pointerId === steerId) {
        var dx = e.clientX - steerOx, dy = e.clientY - steerOy;
        var len = Math.hypot(dx, dy);
        if (len < DRAG_DEAD_PX) { self.touch.x = 0; self.touch.z = 0; return; }
        self.touch.x = dx / len; self.touch.z = dy / len; // screen right/down = world +x/+z (dpad mapping)
      }
    });
    var release = function(e){
      if (!(e.pointerId in pts)) return;
      delete pts[e.pointerId];
      ptCount--;
      if (e.pointerId === steerId) stopSteer();
      if (ptCount >= 2) {
        rebasePinch(); // 3→2 fingers: re-anchor so zoom doesn't jump
      } else if (ptCount === 1) {
        // Pinch ended with one finger down — it becomes a fresh steer origin (no jump).
        for (var id in pts) { steerId = Number(id); steerOx = pts[id].x; steerOy = pts[id].y; }
      }
    };
    hudEl.addEventListener('pointerup', release);
    hudEl.addEventListener('pointercancel', release);
  };

  MazeGame.prototype.renderAvatars = function(){
    var sel = this.state.avatarIndex;
    for (var i = 0; i < this.avatarEls.length; i++){
      var tile = this.avatarEls[i];
      tile.style.borderColor = i === sel ? '#c6f24e' : '#23414f';
      var name = tile.querySelector('.av-name');
      name.style.background = i === sel ? '#c6f24e' : '#16283200';
      name.style.color = i === sel ? '#132018' : '#8fb0a4';
    }
  };

  MazeGame.prototype.renderHud = function(){
    var s = this.state;
    // screen visibility
    el('screen-title').classList.toggle('hidden', s.screen !== 'title');
    el('screen-intro').classList.toggle('hidden', s.screen !== 'intro');
    el('screen-complete').classList.toggle('hidden', s.screen !== 'complete');
    el('screen-over').classList.toggle('hidden', s.screen !== 'over');
    el('hud').classList.toggle('hidden', s.screen !== 'play');

    // title
    el('title-best').textContent = s.best;
    this.renderAvatars();

    // play hud
    el('hud-score').textContent = s.score;
    el('hud-best').textContent = 'BEST ' + s.best;
    var secs = Math.max(0, Math.ceil(s.timeLeft));
    el('hud-time').textContent = secs < 60 ? '0:' + String(secs).padStart(2, '0') : Math.floor(secs / 60) + ':' + String(secs % 60).padStart(2, '0');
    el('hud-time').style.color = secs <= 15 ? '#ff4d5e' : '#eef5ea';
    el('hud-level').textContent = s.level;
    el('hud-total').textContent = this.get_levelTotal();

    var objText, objColor;
    if (s.monstersLeft > 0) { objText = s.monstersLeft + ' MONSTERS LEFT'; objColor = '#8a56c8'; }
    else if (this.boss && this.boss.alive) { objText = 'BOSS: ' + s.bossHp + ' HITS'; objColor = '#ff7a2f'; }
    else { objText = 'GATE OPEN — RUN!'; objColor = '#c6f24e'; }
    var obj = el('hud-obj'); obj.textContent = objText; obj.style.color = objColor; obj.style.borderColor = objColor;

    // hearts
    var maxSlots = Math.max(this.get_maxLives(), s.lives);
    var hearts = '';
    for (var i = 0; i < maxSlots; i++) hearts += '<div style="width:14px;height:14px;background:' + (i < s.lives ? '#ff4d5e' : '#2a3a44') + '"></div>';
    el('hud-hearts').innerHTML = hearts;

    // balls
    var ballCount = this.balls ? this.balls.length : 2;
    var balls = '';
    for (var b = 0; b < ballCount; b++) balls += '<div style="width:13px;height:13px;border-radius:50%;background:' + (b < s.heldBalls ? '#f4f4ef' : '#2a3a44') + '"></div>';
    el('hud-balls').innerHTML = balls;

    // mute
    el('btn-mute').textContent = s.muted ? 'SOUND OFF' : 'SOUND ON';
    el('btn-mute').style.color = s.muted ? '#5f7d8c' : '#c6f24e';

    // intro / complete / over text
    var idx = Math.min(s.level - 1, 4);
    el('intro-level').textContent = s.level;
    el('intro-total').textContent = this.get_levelTotal();
    el('intro-name').textContent = LEVEL_NAMES[idx];
    el('intro-brief').textContent = LEVEL_BRIEFS[idx];
    el('complete-line').textContent = 'Time bonus banked. ' + (s.level >= this.get_levelTotal() ? 'One more push.' : 'The next maze is bigger.');
    el('complete-score').textContent = s.score;
    el('over-title').textContent = s.won ? 'RUN COMPLETE' : 'GAME OVER';
    el('over-title').style.color = s.won ? '#c6f24e' : '#ff4d5e';
    el('over-line').textContent = s.won ? 'Every maze cleared, every monster smashed. Zporti legend.' : (s.lives <= 0 ? 'The monsters got you. Kick first next time.' : 'The clock ran out. Grab more clocks.');
    el('over-score').textContent = s.score;
    el('over-best').textContent = s.best;
  };

  MazeGame.prototype.pop = function(text, color){
    var wrap = el('hud-popups');
    var d = document.createElement('div');
    d.className = 'bungee';
    d.style.cssText = 'font-size:20px;color:' + (color || '#c6f24e') + ';text-shadow:0 2px 0 #0b131a;animation:zpop 1.4s ease-out forwards;white-space:nowrap';
    d.textContent = text;
    wrap.appendChild(d);
    while (wrap.children.length > 5) wrap.removeChild(wrap.firstChild);
    setTimeout(function(){ if (d.parentNode) d.parentNode.removeChild(d); }, 1400);
  };
  MazeGame.prototype.addScore = function(n){ this.setState(function(s){ return { score: s.score + n }; }); };

  MazeGame.prototype.pickAvatar = function(i){
    var self = this;
    this.setState({ avatarIndex: i }, function(){
      if (self.ready && self.playerObj){
        self.world.remove(self.playerObj);
        self.playerObj = self.buildMascot(AVATARS[i]);
        self.playerObj.scale.setScalar(0.78);
        self.playerObj.position.set(self.player.x, 0, self.player.z);
        self.world.add(self.playerObj);
      }
    });
  };

  /* -------- lifecycle -------- */
  MazeGame.prototype.mount = function(){
    var self = this;
    var best = 0;
    try { best = parseInt(localStorage.getItem('zporti-maze-best') || '0', 10) || 0; } catch (e) {}
    this.mountDom();
    this.setState({ best: best, lives: this.get_maxLives() });

    this.onKey = function(e){
      var k = e.key.toLowerCase();
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].indexOf(k) >= 0) e.preventDefault();
      if (e.type === 'keydown'){
        self.keys[k] = true;
        if ((k === ' ' || k === 'x' || k === 'f') && self.state.screen === 'play'){ self.resumeAudio(); self.kick(); }
        if (k === '-' || k === '_') self.zoom = Math.min(2.2, self.zoom + 0.15);
        if (k === '=' || k === '+') self.zoom = Math.max(0.7, self.zoom - 0.15);
        if (k === 'm') self.setState(function(s){ return { muted: !s.muted }; });
        if (k === 'enter' || k === ' '){
          if (self.state.screen === 'intro') self.beginLevel();
          else if (self.state.screen === 'complete') self.nextLevel();
        }
      } else self.keys[k] = false;
    };
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKey);
    this.onWheel = function(e){ e.preventDefault(); self.zoom = Math.max(0.7, Math.min(2.2, self.zoom + (e.deltaY > 0 ? 0.08 : -0.08))); };
    window.addEventListener('wheel', this.onWheel, { passive: false });
    this.onResize = function(){ self.resize(); };
    window.addEventListener('resize', this.onResize);
    this.waitForThree();
  };

  MazeGame.prototype.waitForThree = function(){
    var self = this;
    var mount = el('maze-mount');
    if (!window.THREE || !mount){ setTimeout(function(){ self.waitForThree(); }, 80); return; }
    try { this.setupScene(mount); }
    catch (e) { window.__errs.push('setup: ' + String(e)); post({ type: 'load-error' }); }
  };

  MazeGame.prototype.setupScene = function(mount){
    var T = window.THREE;
    this.scene = new T.Scene();
    this.scene.background = new T.Color(0x0b131a);
    this.scene.fog = new T.Fog(0x0b131a, 62, 140);
    this.camera = new T.PerspectiveCamera(52, 1, 0.5, 400);
    this.renderer = new T.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    mount.appendChild(this.renderer.domElement);

    this.scene.add(new T.HemisphereLight(0xeaf7ee, 0x233a2e, 1.05));
    var sun = new T.DirectionalLight(0xffffff, 0.95);
    sun.position.set(14, 26, 10);
    this.scene.add(sun);
    this.scene.add(new T.AmbientLight(0x334455, 0.5));

    this.world = new T.Group();
    this.scene.add(this.world);

    this.geo = { box: new T.BoxGeometry(1, 1, 1), ball: new T.SphereGeometry(0.5, 14, 12) };
    this.mat = {
      wall: new T.MeshLambertMaterial({ color: 0x1a2f2a }),
      wallTop: new T.MeshLambertMaterial({ color: 0x24413a }),
      void: new T.MeshBasicMaterial({ color: 0x0b1418 }),
      floorA: new T.MeshLambertMaterial({ color: 0x3d8062 }),
      floorB: new T.MeshLambertMaterial({ color: 0x357056 }),
      ball: new T.MeshLambertMaterial({ color: 0xf4f4ef }),
      coin: new T.MeshLambertMaterial({ color: 0xf2c23a }),
      clock: new T.MeshLambertMaterial({ color: 0x4fc0e8 }),
      heart: new T.MeshLambertMaterial({ color: 0xff4d5e }),
      extra: new T.MeshLambertMaterial({ color: 0xc6f24e }),
      patrol: new T.MeshLambertMaterial({ color: 0x8a56c8 }),
      chase: new T.MeshLambertMaterial({ color: 0xd83a4a }),
      spit: new T.MeshLambertMaterial({ color: 0x18a89c }),
      boss: new T.MeshLambertMaterial({ color: 0x1b1f2a }),
      eye: new T.MeshLambertMaterial({ color: 0xfff4c2 }),
      shadow: new T.MeshBasicMaterial({ color: 0xc6f24e, transparent: true, opacity: 0.5 }),
      gate: new T.MeshLambertMaterial({ color: 0x4a5560 }),
      gateOpen: new T.MeshLambertMaterial({ color: 0xc6f24e }),
      proj: new T.MeshLambertMaterial({ color: 0x7ff0e2 })
    };

    this.ready = true;
    window.__mazeGame = this;
    this.resize();
    this.clock = new T.Clock();
    this.buildLevel(1);
    this.loop();
    post({ type: 'ready' });
  };

  MazeGame.prototype.resize = function(){
    if (!this.renderer) return;
    var mount = el('maze-mount');
    if (!mount) return;
    var w = mount.clientWidth || window.innerWidth;
    var h = mount.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h);
    this.renderer.domElement.style.display = 'block';
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  };

  /* -------- maze -------- */
  MazeGame.prototype.genMaze = function(n){
    var g = [], i, j;
    for (j = 0; j < n; j++){ g.push([]); for (i = 0; i < n; i++) g[j].push(1); }
    var stack = [[1, 1]];
    g[1][1] = 0;
    var dirs = [[0, -2], [0, 2], [-2, 0], [2, 0]];
    while (stack.length){
      var cur = stack[stack.length - 1];
      var opts = [];
      for (var d = 0; d < 4; d++){
        var ni = cur[0] + dirs[d][0], nj = cur[1] + dirs[d][1];
        if (ni > 0 && nj > 0 && ni < n - 1 && nj < n - 1 && g[nj][ni] === 1) opts.push([ni, nj, cur[0] + dirs[d][0] / 2, cur[1] + dirs[d][1] / 2]);
      }
      if (!opts.length){ stack.pop(); continue; }
      var p = opts[(Math.random() * opts.length) | 0];
      g[p[3]][p[2]] = 0; g[p[1]][p[0]] = 0;
      stack.push([p[0], p[1]]);
    }
    var extra = Math.floor(n * n * 0.085);
    while (extra > 0){
      var ei = 1 + ((Math.random() * (n - 2)) | 0), ej = 1 + ((Math.random() * (n - 2)) | 0);
      if (g[ej][ei] === 1){ g[ej][ei] = 0; extra--; }
    }
    var rooms = 3 + Math.floor(n / 10);
    for (var r = 0; r < rooms; r++){
      var rw = 3 + ((Math.random() * 4) | 0), rh = 3 + ((Math.random() * 4) | 0);
      var i0 = 1 + ((Math.random() * (n - rw - 2)) | 0), j0 = 1 + ((Math.random() * (n - rh - 2)) | 0);
      for (var jj = j0; jj < j0 + rh; jj++) for (var ii = i0; ii < i0 + rw; ii++) g[jj][ii] = 0;
    }
    this.removeDeadEnds(g, n);
    return g;
  };

  MazeGame.prototype.removeDeadEnds = function(g, n){
    var D = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (var pass = 0; pass < 14; pass++){
      var changed = false;
      for (var j = 1; j < n - 1; j++) for (var i = 1; i < n - 1; i++){
        if (g[j][i] !== 0) continue;
        var open = D.filter(function(d){ return g[j + d[1]][i + d[0]] === 0; }).length;
        if (open > 1) continue;
        var cand = D.filter(function(d){ var ni = i + d[0], nj = j + d[1]; return ni > 0 && nj > 0 && ni < n - 1 && nj < n - 1 && g[nj][ni] === 1; });
        if (cand.length){ var c = cand[(Math.random() * cand.length) | 0]; g[j + c[1]][i + c[0]] = 0; changed = true; }
      }
      if (!changed) break;
    }
  };

  MazeGame.prototype.isWall = function(i, j){
    if (!this.grid || j < 0 || i < 0 || j >= this.N || i >= this.N) return true;
    return this.grid[j][i] === 1;
  };
  MazeGame.prototype.cellToWorld = function(i, j){ var o = ((this.N - 1) / 2) * this.CS; return { x: i * this.CS - o, z: j * this.CS - o }; };
  MazeGame.prototype.worldToCell = function(x, z){ var o = ((this.N - 1) / 2) * this.CS; return { i: Math.round((x + o) / this.CS), j: Math.round((z + o) / this.CS) }; };
  MazeGame.prototype.openCells = function(){
    var list = [];
    for (var j = 1; j < this.N - 1; j++) for (var i = 1; i < this.N - 1; i++) if (this.grid[j][i] === 0) list.push([i, j]);
    return list;
  };
  MazeGame.prototype.bfs = function(from, to){
    var q = [from], seen = {}, prev = {};
    seen[from[0] + ',' + from[1]] = true;
    var D = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    while (q.length){
      var c = q.shift();
      if (c[0] === to[0] && c[1] === to[1]){
        var k = c[0] + ',' + c[1], path = [c];
        while (prev[k]){ path.unshift(prev[k]); k = prev[k][0] + ',' + prev[k][1]; }
        return path;
      }
      for (var d = 0; d < 4; d++){
        var ni = c[0] + D[d][0], nj = c[1] + D[d][1], key = ni + ',' + nj;
        if (!seen[key] && !this.isWall(ni, nj)){ seen[key] = true; prev[key] = c; q.push([ni, nj]); }
      }
    }
    return null;
  };

  MazeGame.prototype.clearGroup = function(){
    var self = this;
    while (this.world.children.length){
      var c = this.world.children.pop();
      c.traverse(function(o){ if (o.geometry && o.geometry !== self.geo.box && o.geometry !== self.geo.ball) o.geometry.dispose(); });
    }
  };

  /* -------- build -------- */
  MazeGame.prototype.buildLevel = function(lv){
    var T = window.THREE, self = this;
    this.clearGroup();
    var n = (SIZES[this.props.mapSize] || SIZES.large)[Math.min(lv - 1, 4)];
    this.N = n;
    this.grid = this.genMaze(n);
    var span = n * this.CS;

    var floor = new T.Mesh(new T.PlaneGeometry(span + 900, span + 900), this.mat.void);
    floor.rotation.x = -Math.PI / 2; floor.position.y = -0.4;
    this.world.add(floor);
    var tile = new T.PlaneGeometry(this.CS, this.CS);
    var fA = [], fB = [], wallCells = [];
    for (var j = 0; j < n; j++) for (var i = 0; i < n; i++){
      if (this.grid[j][i] === 1){ wallCells.push([i, j]); continue; }
      ((i + j) % 2 === 0 ? fA : fB).push([i, j]);
    }
    var dummy = new T.Object3D();
    var instance = function(geo, mat, cells, place){
      if (!cells.length) return;
      var im = new T.InstancedMesh(geo, mat, cells.length);
      cells.forEach(function(c, k){ place(dummy, c); dummy.updateMatrix(); im.setMatrixAt(k, dummy.matrix); });
      im.instanceMatrix.needsUpdate = true;
      self.world.add(im);
    };
    var floorPlace = function(d, c){ var w = self.cellToWorld(c[0], c[1]); d.rotation.set(-Math.PI / 2, 0, 0); d.scale.set(1, 1, 1); d.position.set(w.x, 0, w.z); };
    instance(tile, this.mat.floorA, fA, floorPlace);
    instance(tile, this.mat.floorB, fB, floorPlace);
    var wallH = 2.5;
    instance(this.geo.box, this.mat.wall, wallCells, function(d, c){ var w = self.cellToWorld(c[0], c[1]); d.rotation.set(0, 0, 0); d.scale.set(self.CS, wallH, self.CS); d.position.set(w.x, wallH / 2, w.z); });
    instance(this.geo.box, this.mat.wallTop, wallCells, function(d, c){ var w = self.cellToWorld(c[0], c[1]); d.rotation.set(0, 0, 0); d.scale.set(self.CS, 0.4, self.CS); d.position.set(w.x, wallH + 0.2, w.z); });

    // player
    this.player = { cell: [1, 1], x: 0, z: 0, vx: 0, vz: 0, face: 0, invuln: 2.6, step: 0 };
    var sp = this.cellToWorld(1, 1);
    this.player.x = sp.x; this.player.z = sp.z;
    this.playerObj = this.buildMascot(AVATARS[this.state.avatarIndex]);
    this.playerObj.scale.setScalar(0.78);
    this.playerObj.position.set(sp.x, 0, sp.z);
    this.world.add(this.playerObj);
    this.playerShadow = new T.Mesh(new T.RingGeometry(1.0, 1.45, 26), this.mat.shadow);
    this.playerShadow.rotation.x = -Math.PI / 2;
    this.world.add(this.playerShadow);

    // exit gate: farthest open cell from start
    var cells = this.openCells();
    var far = cells[0], farD = -1;
    for (var ci = 0; ci < cells.length; ci++){ var cc = cells[ci]; var dd = Math.abs(cc[0] - 1) + Math.abs(cc[1] - 1); if (dd > farD){ farD = dd; far = cc; } }
    this.exitCell = far;
    var ew = this.cellToWorld(far[0], far[1]);
    this.gate = new T.Group();
    for (var s = -1; s <= 1; s += 2){ var post2 = new T.Mesh(this.geo.box, this.mat.gate); post2.scale.set(0.6, 4, 0.6); post2.position.set(s * 1.3, 2, 0); this.gate.add(post2); }
    var bar = new T.Mesh(this.geo.box, this.mat.gate); bar.scale.set(3.2, 0.6, 0.6); bar.position.y = 4; this.gate.add(bar);
    this.gateGlow = new T.Mesh(new T.CircleGeometry(1.6, 20), this.mat.gate);
    this.gateGlow.rotation.x = -Math.PI / 2; this.gateGlow.position.y = 0.05;
    this.gate.add(this.gateGlow);
    this.gate.position.set(ew.x, 0, ew.z);
    this.world.add(this.gate);

    // spawn helpers
    var used = {}; used['1,1'] = true; used[far[0] + ',' + far[1]] = true;
    var pickCell = function(minDist){
      for (var t = 0; t < 260; t++){
        var c = cells[(Math.random() * cells.length) | 0];
        var key = c[0] + ',' + c[1];
        if (used[key]) continue;
        if (Math.abs(c[0] - 1) + Math.abs(c[1] - 1) < (minDist || 0)) continue;
        used[key] = true; return c;
      }
      return cells[(Math.random() * cells.length) | 0];
    };

    // monsters
    this.monsters = [];
    var count = Math.round((10 + lv * 5) * this.get_diff());
    for (var m = 0; m < count; m++){
      var kind = m % 3 === 0 ? 'patrol' : m % 3 === 1 ? 'chase' : 'spit';
      this.monsters.push(this.makeMonster(kind, pickCell(9), lv));
    }
    var bc = pickCell(Math.floor(n * 0.8));
    this.boss = this.makeMonster('boss', bc, lv);
    this.boss.hp = 2 + lv;
    this.boss.awake = false;
    this.monstersTotal = count;

    // pickups
    this.pickups = [];
    var addPickup = function(kind, mat, h, scale){
      var c = pickCell(2);
      var w = self.cellToWorld(c[0], c[1]);
      var mesh = new T.Mesh(kind === 'coin' ? self.geo.ball : self.geo.box, mat);
      mesh.scale.set(scale, scale, scale);
      mesh.position.set(w.x, h, w.z);
      self.world.add(mesh);
      self.pickups.push({ kind: kind, obj: mesh, x: w.x, z: w.z, y: h, alive: true });
    };
    for (var pc0 = 0; pc0 < 26 + lv * 8; pc0++) addPickup('coin', this.mat.coin, 1, 0.9);
    for (var pc1 = 0; pc1 < 6 + lv; pc1++) addPickup('clock', this.mat.clock, 1.1, 1.1);
    for (var pc2 = 0; pc2 < 4; pc2++) addPickup('extra', this.mat.extra, 1, 0.8);
    for (var pc3 = 0; pc3 < 1 + lv; pc3++) addPickup('heart', this.mat.heart, 1.1, 1);

    // balls
    this.balls = [];
    this.projectiles = [];
    for (var b = 0; b < 2; b++) this.addBall();

    this.gateChimed = false;
    this.camPos = new T.Vector3(sp.x, 30, sp.z + 22);
    this.setState({ level: lv, monstersLeft: count, bossAwake: false, bossHp: this.boss.hp, timeLeft: this.get_startSeconds() + (lv - 1) * 45, heldBalls: 2 });
  };

  MazeGame.prototype.addBall = function(){
    var T = window.THREE;
    var m = new T.Mesh(this.geo.ball, this.mat.ball);
    m.scale.set(1.1, 1.1, 1.1);
    this.world.add(m);
    this.balls.push({ obj: m, state: 'held', x: this.player.x, z: this.player.z, vx: 0, vz: 0, life: 0, combo: 0 });
  };

  MazeGame.prototype.buildMascot = function(av){
    var T = window.THREE, self = this;
    var g = new T.Group();
    var M = function(hex){ return new T.MeshLambertMaterial({ color: new T.Color(hex) }); };
    var mBall = M(av.ball), mPatch = M(av.patch), mHair = M(av.hair), mJersey = M(av.jersey), mTrim = M(av.trim), mSkin = M('#c98d5e');
    var head = new T.Mesh(this.geo.box, mBall); head.scale.set(1.7, 1.7, 1.7); head.position.y = 2.7; g.add(head);
    var patches = [[0.86, 0, 0.4], [-0.86, 0, 0.4], [0, -0.5, 0.86]];
    for (var pi = 0; pi < patches.length; pi++){
      var p = patches[pi];
      var pt = new T.Mesh(this.geo.box, mPatch);
      pt.scale.set(0.55, 0.55, 0.12);
      if (Math.abs(p[0]) > 0.5){ pt.scale.set(0.12, 0.55, 0.55); }
      if (Math.abs(p[1]) > 0.5 && p[1] > 0){ pt.scale.set(0.55, 0.12, 0.55); }
      pt.position.set(p[0], 2.7 + p[1], p[2] * 1.02);
      if (Math.abs(p[0]) > 0.5) pt.position.set(p[0], 2.7, p[2]);
      g.add(pt);
    }
    var hair = new T.Mesh(this.geo.box, mHair); hair.scale.set(1.85, 0.5, 1.85); hair.position.y = 3.7; g.add(hair);
    var brim = new T.Mesh(this.geo.box, mHair); brim.scale.set(1.6, 0.25, 0.7); brim.position.set(0, 3.5, 1.1); g.add(brim);
    var torso = new T.Mesh(this.geo.box, mJersey); torso.scale.set(1.5, 1.5, 0.9); torso.position.y = 1.4; g.add(torso);
    var stripe = new T.Mesh(this.geo.box, mTrim); stripe.scale.set(1.55, 0.3, 0.95); stripe.position.y = 0.8; g.add(stripe);
    var shoulder = new T.Mesh(this.geo.box, mTrim); shoulder.scale.set(2.1, 0.22, 1.3); shoulder.position.y = 2.05; g.add(shoulder);
    var arms = [];
    [-1, 1].forEach(function(s){ var a = new T.Mesh(self.geo.box, mSkin); a.scale.set(0.4, 1.2, 0.4); a.position.set(s * 1, 1.4, 0); g.add(a); arms.push(a); });
    var legs = [];
    [-1, 1].forEach(function(s){ var l = new T.Mesh(self.geo.box, mTrim); l.scale.set(0.5, 1.3, 0.5); var pivot = new T.Group(); pivot.position.set(0, 0.65, 0); l.position.set(s * 0.4, -0.65, 0); pivot.add(l); g.add(pivot); legs.push(pivot); });
    g.userData = { legs: legs, arms: arms };
    return g;
  };

  MazeGame.prototype.makeMonster = function(kind, cell, lv){
    var T = window.THREE, self = this;
    var w = this.cellToWorld(cell[0], cell[1]);
    var g = new T.Group();
    var mat = this.mat[kind];
    var big = kind === 'boss' ? 2.1 : 1;
    var body = new T.Mesh(this.geo.box, mat); body.scale.set(1.6 * big, 1.5 * big, 1.6 * big); body.position.y = 0.8 * big; g.add(body);
    var top = new T.Mesh(this.geo.box, mat); top.scale.set(1.1 * big, 0.7 * big, 1.1 * big); top.position.y = 1.85 * big; g.add(top);
    [-1, 1].forEach(function(s){ var e = new T.Mesh(self.geo.box, self.mat.eye); e.scale.set(0.3 * big, 0.3 * big, 0.12); e.position.set(s * 0.38 * big, 1.0 * big, 0.82 * big); g.add(e); });
    if (kind === 'spit' || kind === 'boss'){ var horn = new T.Mesh(this.geo.box, this.mat.eye); horn.scale.set(0.25 * big, 0.9 * big, 0.25 * big); horn.position.set(0, 2.4 * big, 0); g.add(horn); }
    g.position.set(w.x, 0, w.z);
    this.world.add(g);
    var speed = (kind === 'chase' ? 4.4 : kind === 'patrol' ? 3.4 : 0) * (0.85 + lv * 0.06) * this.get_diff();
    var dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    return { kind: kind, obj: g, x: w.x, z: w.z, cell: cell, speed: speed, alive: true, dir: dirs[(Math.random() * 4) | 0], cool: Math.random() * 2, path: null, repath: 0, hp: 1, awake: kind !== 'boss', bob: Math.random() * 6, big: big };
  };

  /* -------- flow -------- */
  MazeGame.prototype.start = function(){
    var self = this;
    this.reported = false;
    this.run = { coinsCollected: 0, levelsCleared: 0 };
    this.setState({ screen: 'intro', score: 0, lives: this.get_maxLives(), level: 1 }, function(){ self.buildLevel(1); });
  };
  MazeGame.prototype.beginLevel = function(){
    this.setState({ screen: 'play' });
    // The controls hint sits mid-screen — show it briefly at each level start, then fade it out
    // so it never lingers over the action.
    var hint = el('hud-keys-hint');
    hint.classList.remove('gone');
    clearTimeout(this.hintFadeTO);
    this.hintFadeTO = setTimeout(function(){ hint.classList.add('gone'); }, 4000);
  };
  MazeGame.prototype.nextLevel = function(){
    var self = this;
    var lv = this.state.level + 1;
    if (lv > this.get_levelTotal()){ this.finish(true); return; }
    this.setState({ screen: 'intro', level: lv }, function(){ self.buildLevel(lv); });
  };
  MazeGame.prototype.restart = function(){ this.start(); };

  MazeGame.prototype.finish = function(won){
    this.sfx(won ? 'clear' : 'over');
    var best = this.state.best;
    if (this.state.score > best){ best = this.state.score; try { localStorage.setItem('zporti-maze-best', String(best)); } catch (e) {} }
    this.setState({ screen: 'over', won: won, best: best });
    this.reportRunEnd(won);
  };

  MazeGame.prototype.reportRunEnd = function(won){
    if (this.reported) return;
    this.reported = true;
    var outcome = won ? 'win' : (this.state.lives <= 0 ? 'dead' : 'timeout');
    post({
      type: 'run-end',
      outcome: outcome,
      levelsCleared: this.run.levelsCleared,
      coinsCollected: this.run.coinsCollected,
      score: this.state.score,
      best: this.state.best
    });
  };

  /* -------- audio -------- */
  MazeGame.prototype.resumeAudio = function(){ var ac = this.audioCtx(); if (ac && ac.state === 'suspended') ac.resume(); };
  MazeGame.prototype.audioCtx = function(){
    if (this.state.muted) return null;
    if (!this.ac){
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      this.ac = new AC();
      this.master = this.ac.createGain();
      this.master.gain.value = 0.28;
      this.master.connect(this.ac.destination);
    }
    if (this.ac.state === 'suspended') this.ac.resume();
    return this.ac;
  };
  MazeGame.prototype.tone = function(f0, f1, dur, type, vol, delay){
    var ac = this.audioCtx(); if (!ac) return;
    var t = ac.currentTime + (delay || 0);
    var o = ac.createOscillator(), gg = ac.createGain();
    o.type = type || 'square';
    o.frequency.setValueAtTime(f0, t);
    if (f1 && f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    gg.gain.setValueAtTime(0.0001, t);
    gg.gain.exponentialRampToValueAtTime(vol || 0.3, t + 0.012);
    gg.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(gg); gg.connect(this.master);
    o.start(t); o.stop(t + dur + 0.02);
  };
  MazeGame.prototype.noise = function(dur, vol, delay){
    var ac = this.audioCtx(); if (!ac) return;
    var t = ac.currentTime + (delay || 0);
    var len = Math.floor(ac.sampleRate * dur);
    var buf = ac.createBuffer(1, len, ac.sampleRate);
    var dch = buf.getChannelData(0);
    for (var i = 0; i < len; i++) dch[i] = (Math.random() * 2 - 1) * (1 - i / len);
    var src = ac.createBufferSource(); src.buffer = buf;
    var gg = ac.createGain(); gg.gain.value = vol || 0.25;
    src.connect(gg); gg.connect(this.master);
    src.start(t);
  };
  MazeGame.prototype.sfx = function(name){
    switch (name){
      case 'kick': this.tone(320, 90, 0.16, 'square', 0.34); this.noise(0.06, 0.14); break;
      case 'bounce': this.tone(700, 500, 0.05, 'square', 0.12); break;
      case 'smash': this.noise(0.18, 0.3); this.tone(180, 60, 0.22, 'sawtooth', 0.22); break;
      case 'coin': this.tone(880, 880, 0.06, 'square', 0.2); this.tone(1320, 1320, 0.1, 'square', 0.18, 0.06); break;
      case 'clock': this.tone(600, 1200, 0.16, 'triangle', 0.22); break;
      case 'heart': this.tone(520, 780, 0.1, 'triangle', 0.24); this.tone(1040, 1040, 0.14, 'triangle', 0.18, 0.1); break;
      case 'extra': this.tone(440, 660, 0.12, 'square', 0.2); break;
      case 'hurt': this.tone(300, 70, 0.34, 'sawtooth', 0.3); this.noise(0.2, 0.2); break;
      case 'spit': this.tone(240, 520, 0.12, 'sawtooth', 0.14); break;
      case 'boss': this.tone(90, 55, 0.7, 'sawtooth', 0.32); this.tone(140, 80, 0.7, 'square', 0.16, 0.06); break;
      case 'bosshit': this.noise(0.14, 0.28); this.tone(140, 90, 0.2, 'square', 0.26); break;
      case 'gate': [523, 659, 784].forEach(function(f, i){ this.tone(f, f, 0.18, 'triangle', 0.2, i * 0.07); }, this); break;
      case 'clear': [523, 659, 784, 1046].forEach(function(f, i){ this.tone(f, f, 0.2, 'square', 0.22, i * 0.1); }, this); break;
      case 'over': [440, 349, 262].forEach(function(f, i){ this.tone(f, f * 0.98, 0.34, 'sawtooth', 0.26, i * 0.18); }, this); break;
      default: break;
    }
  };

  /* -------- actions -------- */
  MazeGame.prototype.kick = function(){
    if (!this.balls) return;
    var held = this.balls.filter(function(b){ return b.state === 'held'; });
    if (!held.length){
      var live = this.balls.filter(function(b){ return b.state === 'live'; });
      if (live.length){ live[0].state = 'return'; }
      return;
    }
    var b = held[0];
    var a = this.player.face;
    b.state = 'live';
    b.x = this.player.x + Math.sin(a) * 1.4;
    b.z = this.player.z + Math.cos(a) * 1.4;
    b.vx = Math.sin(a) * 30; b.vz = Math.cos(a) * 30;
    b.life = 0; b.combo = 0;
    this.sfx('kick');
    this.setState({ heldBalls: this.balls.filter(function(x){ return x.state === 'held'; }).length });
  };

  MazeGame.prototype.hurt = function(cause){
    if (this.player.invuln > 0) return;
    this.player.invuln = 1.7;
    this.sfx('hurt');
    var lives = this.state.lives - 1;
    this.pop('-1 LIFE', '#ff4d5e');
    if (lives <= 0){ this.setState({ lives: 0, deathCause: cause }); this.finish(false); }
    else this.setState({ lives: lives, deathCause: cause });
  };

  MazeGame.prototype.resolve = function(px, pz, r){
    var c = this.worldToCell(px, pz);
    var x = px, z = pz, hitX = false, hitZ = false;
    for (var dj = -1; dj <= 1; dj++) for (var di = -1; di <= 1; di++){
      var i = c.i + di, j = c.j + dj;
      if (!this.isWall(i, j)) continue;
      var w = this.cellToWorld(i, j), half = this.CS / 2 + r;
      var dx = x - w.x, dz = z - w.z;
      if (Math.abs(dx) < half && Math.abs(dz) < half){
        var ox = half - Math.abs(dx), oz = half - Math.abs(dz);
        if (ox < oz){ x = w.x + Math.sign(dx || 1) * half; hitX = true; }
        else { z = w.z + Math.sign(dz || 1) * half; hitZ = true; }
      }
    }
    return { x: x, z: z, hitX: hitX, hitZ: hitZ };
  };

  /* -------- loop -------- */
  MazeGame.prototype.loop = function(){
    if (this.stopped) return;
    var self = this;
    requestAnimationFrame(function(){ self.loop(); });
    var dt = Math.min(this.clock.getDelta(), 0.05);
    if (this.state.screen === 'play') this.update(dt);
    if (this.playerObj) this.updateCamera(dt);
    this.renderer.render(this.scene, this.camera);
    this.drawMinimap();
  };

  MazeGame.prototype.updateCamera = function(dt){
    var T = window.THREE;
    var z = this.zoom || 1;
    var target = new T.Vector3(this.player.x, 0, this.player.z + 20 * z);
    target.y = 30 * z;
    this.camPos.lerp(target, 1 - Math.pow(0.001, dt));
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.player.x, 1.4, this.player.z + 0.5);
  };

  MazeGame.prototype.update = function(dt){
    var self = this;
    var p = this.player;
    var t = this.state.timeLeft - dt;
    if (t <= 0){ this.setState({ timeLeft: 0 }); this.finish(false); return; }
    if (Math.floor(t) !== Math.floor(this.state.timeLeft)) this.setState({ timeLeft: t });
    else this.state.timeLeft = t;

    // input
    var ix = this.touch.x, iz = this.touch.z;
    var k = this.keys;
    if (k['a'] || k['arrowleft']) ix -= 1;
    if (k['d'] || k['arrowright']) ix += 1;
    if (k['w'] || k['arrowup']) iz -= 1;
    if (k['s'] || k['arrowdown']) iz += 1;
    var len = Math.hypot(ix, iz);
    if (len > 0){ ix /= len; iz /= len; p.face = Math.atan2(ix, iz); }
    var sp = 11;
    var r = this.resolve(p.x + ix * sp * dt, p.z, 0.85); p.x = r.x;
    r = this.resolve(p.x, p.z + iz * sp * dt, 0.85); p.z = r.z;
    p.step += len * dt * 9;
    p.invuln = Math.max(0, p.invuln - dt);

    this.playerObj.position.set(p.x, 0, p.z);
    this.playerObj.rotation.y = p.face;
    var legs = this.playerObj.userData.legs, arms = this.playerObj.userData.arms;
    legs[0].rotation.x = Math.sin(p.step) * 0.8 * (len > 0 ? 1 : 0);
    legs[1].rotation.x = -Math.sin(p.step) * 0.8 * (len > 0 ? 1 : 0);
    arms[0].rotation.x = -Math.sin(p.step) * 0.6;
    arms[1].rotation.x = Math.sin(p.step) * 0.6;
    this.playerObj.visible = !(p.invuln > 0 && Math.floor(p.invuln * 12) % 2 === 0);
    this.playerShadow.position.set(p.x, 0.04, p.z);

    // balls
    var heldIdx = 0;
    for (var bi = 0; bi < this.balls.length; bi++){
      var b = this.balls[bi];
      if (b.state === 'held'){
        var a = p.face + (heldIdx === 0 ? 0 : heldIdx === 1 ? 0.7 : -0.7);
        var d = heldIdx === 0 ? 1.5 : 1.2;
        b.x += (p.x + Math.sin(a) * d - b.x) * Math.min(1, dt * 14);
        b.z += (p.z + Math.cos(a) * d - b.z) * Math.min(1, dt * 14);
        b.obj.position.set(b.x, 0.55, b.z);
        b.obj.rotation.x += (Math.abs(ix) + Math.abs(iz)) * dt * 6;
        heldIdx++;
        continue;
      }
      if (b.state === 'return'){
        b.x += (p.x - b.x) * Math.min(1, dt * 8);
        b.z += (p.z - b.z) * Math.min(1, dt * 8);
        b.obj.position.set(b.x, 0.55 + Math.sin(Date.now() / 120) * 0.1, b.z);
        if (Math.hypot(b.x - p.x, b.z - p.z) < 1.6){ b.state = 'held'; this.setState({ heldBalls: this.balls.filter(function(x){ return x.state === 'held'; }).length }); }
        continue;
      }
      // live
      b.life += dt;
      var nr = this.resolve(b.x + b.vx * dt, b.z, 0.5);
      if (nr.hitX && b.life - (b.lastB || -1) > 0.09){ b.lastB = b.life; this.sfx('bounce'); }
      if (nr.hitX){ b.vx *= -1; } b.x = nr.x;
      nr = this.resolve(b.x, b.z + b.vz * dt, 0.5);
      if (nr.hitZ && b.life - (b.lastB || -1) > 0.09){ b.lastB = b.life; this.sfx('bounce'); }
      if (nr.hitZ){ b.vz *= -1; } b.z = nr.z;
      var speed = Math.hypot(b.vx, b.vz);
      var decay = Math.pow(0.62, dt);
      b.vx *= decay; b.vz *= decay;
      b.obj.position.set(b.x, 0.55, b.z);
      b.obj.rotation.x += speed * dt * 0.6;
      b.obj.rotation.z -= speed * dt * 0.4;
      var all0 = this.monsters.concat([this.boss]);
      for (var mi = 0; mi < all0.length; mi++){
        var mm = all0[mi];
        if (!mm.alive || !mm.awake) continue;
        if (Math.hypot(b.x - mm.x, b.z - mm.z) < 1.1 + mm.big * 0.7){ this.hitMonster(mm, b); b.vx *= -0.7; b.vz *= -0.7; }
      }
      for (var pki = 0; pki < this.pickups.length; pki++){ var pk0 = this.pickups[pki]; if (!pk0.alive) continue; if (Math.hypot(b.x - pk0.x, b.z - pk0.z) < 1.2) this.collect(pk0); }
      if (speed < 4 || b.life > 5) b.state = 'return';
    }

    // monsters
    var pcell = this.worldToCell(p.x, p.z);
    var all = this.monsters.concat([this.boss]);
    for (var ai = 0; ai < all.length; ai++){
      var m = all[ai];
      if (!m.alive) continue;
      m.bob += dt * 4;
      m.obj.position.set(m.x, Math.abs(Math.sin(m.bob)) * 0.22, m.z);
      if (!m.awake){ m.obj.rotation.y += dt * 0.3; m.obj.scale.setScalar(0.9 + Math.sin(m.bob * 0.4) * 0.05); continue; }
      var mc = this.worldToCell(m.x, m.z);
      if (m.kind === 'patrol'){
        var nx = m.x + m.dir[0] * m.speed * dt, nz = m.z + m.dir[1] * m.speed * dt;
        var rr = this.resolve(nx, nz, 0.8);
        if (rr.hitX || rr.hitZ){
          var opts = [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(function(dd){ return !self.isWall(mc.i + dd[0], mc.j + dd[1]); });
          if (opts.length) m.dir = opts[(Math.random() * opts.length) | 0];
          var ctr = this.cellToWorld(mc.i, mc.j); m.x = ctr.x; m.z = ctr.z;
        } else { m.x = rr.x; m.z = rr.z; }
        m.obj.rotation.y = Math.atan2(m.dir[0], m.dir[1]);
      } else if (m.kind === 'chase' || m.kind === 'boss'){
        m.repath -= dt;
        var dist = Math.hypot(p.x - m.x, p.z - m.z);
        if (m.repath <= 0){ m.repath = 0.45; m.path = (dist < (m.kind === 'boss' ? 2000 : 26)) ? this.bfs([mc.i, mc.j], [pcell.i, pcell.j]) : null; }
        if (m.path && m.path.length > 1){
          var nextC = m.path[1];
          var w = this.cellToWorld(nextC[0], nextC[1]);
          var dx = w.x - m.x, dz = w.z - m.z, dmag = Math.hypot(dx, dz) || 1;
          var s2 = (m.kind === 'boss' ? m.speed * 0.72 : m.speed);
          m.x += (dx / dmag) * s2 * dt; m.z += (dz / dmag) * s2 * dt;
          if (dmag < 0.5) m.path.shift();
          m.obj.rotation.y = Math.atan2(dx, dz);
        }
        if (m.kind === 'boss'){ m.cool -= dt; if (m.cool <= 0 && dist < 40){ m.cool = 1.9; this.spit(m, p); } }
      } else if (m.kind === 'spit'){
        m.obj.rotation.y += dt * 0.8;
        m.cool -= dt;
        var dist2 = Math.hypot(p.x - m.x, p.z - m.z);
        if (m.cool <= 0 && dist2 < 26){ m.cool = 1.5 / this.get_diff(); this.spit(m, p); }
      }
      if (Math.hypot(p.x - m.x, p.z - m.z) < 1.3 + m.big * 0.7) this.hurt(m.kind);
    }

    // projectiles
    for (var qi = this.projectiles.length - 1; qi >= 0; qi--){
      var q = this.projectiles[qi];
      q.life += dt;
      var qr = this.resolve(q.x + q.vx * dt, q.z + q.vz * dt, 0.3);
      if (qr.hitX || qr.hitZ || q.life > 4){ this.world.remove(q.obj); this.projectiles.splice(qi, 1); continue; }
      q.x = qr.x; q.z = qr.z; q.obj.position.set(q.x, 1, q.z);
      if (Math.hypot(q.x - p.x, q.z - p.z) < 1.1){ this.world.remove(q.obj); this.projectiles.splice(qi, 1); this.hurt('spit'); }
    }

    // pickups
    for (var pi2 = 0; pi2 < this.pickups.length; pi2++){
      var pk = this.pickups[pi2];
      if (!pk.alive) continue;
      pk.obj.rotation.y += dt * 2.2;
      pk.obj.position.y = pk.y + Math.sin(Date.now() / 320 + pk.x) * 0.16;
      if (Math.hypot(p.x - pk.x, p.z - pk.z) < 1.5) this.collect(pk);
    }

    // gate
    var open = this.state.monstersLeft === 0 && !this.boss.alive;
    if (open && !this.gateChimed){ this.gateChimed = true; this.sfx('gate'); }
    var gw = this.cellToWorld(this.exitCell[0], this.exitCell[1]);
    this.gateGlow.material = open ? this.mat.gateOpen : this.mat.gate;
    this.gate.rotation.y = open ? Math.sin(Date.now() / 500) * 0.12 : 0;
    if (open && Math.hypot(p.x - gw.x, p.z - gw.z) < 2.2) this.completeLevel();

    // wake boss
    if (this.state.monstersLeft === 0 && !this.boss.awake && this.boss.alive){
      this.boss.awake = true; this.boss.obj.scale.setScalar(1);
      this.setState({ bossAwake: true }); this.sfx('boss'); this.pop('BOSS AWAKE!', '#ff7a2f');
    }
  };

  MazeGame.prototype.spit = function(m, p){
    var T = window.THREE;
    var dx = p.x - m.x, dz = p.z - m.z, d = Math.hypot(dx, dz) || 1;
    var o = new T.Mesh(this.geo.ball, this.mat.proj);
    o.scale.setScalar(0.7); o.position.set(m.x, 1, m.z);
    this.world.add(o); this.sfx('spit');
    this.projectiles.push({ obj: o, x: m.x, z: m.z, vx: (dx / d) * 15, vz: (dz / d) * 15, life: 0 });
  };

  MazeGame.prototype.hitMonster = function(m, b){
    var self = this;
    m.hp -= 1; b.combo += 1;
    if (m.hp > 0){
      this.sfx('bosshit');
      this.pop('BOSS HIT ' + m.hp + ' LEFT', '#ff7a2f');
      this.setState({ bossHp: m.hp });
      m.obj.scale.setScalar(1.06);
      setTimeout(function(){ if (m.obj) m.obj.scale.setScalar(1); }, 120);
      return;
    }
    m.alive = false; this.world.remove(m.obj); this.sfx('smash');
    if (m.kind === 'boss'){ this.addScore(250); this.pop('BOSS DOWN +250', '#c6f24e'); this.setState({ bossHp: 0 }); }
    else {
      var gain = 25 * b.combo;
      this.addScore(gain);
      this.pop((b.combo > 1 ? 'COMBO x' + b.combo + '  ' : '') + '+' + gain, b.combo > 1 ? '#ff7a2f' : '#c6f24e');
      var left = this.monsters.filter(function(x){ return x.alive; }).length;
      this.setState({ monstersLeft: left });
    }
  };

  MazeGame.prototype.collect = function(pk){
    var self = this;
    pk.alive = false; this.world.remove(pk.obj);
    this.sfx(pk.kind === 'coin' ? 'coin' : pk.kind);
    if (pk.kind === 'coin'){ this.run.coinsCollected += 1; this.addScore(10); this.pop('+10', '#f2c23a'); }
    else if (pk.kind === 'clock'){ this.setState(function(s){ return { timeLeft: s.timeLeft + 10 }; }); this.pop('+10 SEC', '#4fc0e8'); }
    else if (pk.kind === 'heart'){ this.setState(function(s){ return { lives: Math.min(self.get_maxLives() + 2, s.lives + 1) }; }); this.pop('+1 LIFE', '#ff4d5e'); }
    else if (pk.kind === 'extra'){
      if (this.balls.length < 4){ this.addBall(); this.pop('EXTRA BALL', '#c6f24e'); }
      else { this.addScore(20); this.pop('+20', '#c6f24e'); }
      this.setState({ heldBalls: this.balls.filter(function(x){ return x.state === 'held'; }).length });
    }
  };

  MazeGame.prototype.completeLevel = function(){
    var self = this;
    this.sfx('clear');
    this.run.levelsCleared += 1;
    var bonus = Math.floor(this.state.timeLeft) * 3 + 100;
    var last = this.state.level >= this.get_levelTotal();
    this.setState(function(s){ return { score: s.score + bonus }; }, function(){
      if (last) self.finish(true);
      else self.setState({ screen: 'complete' });
    });
  };

  MazeGame.prototype.drawMinimap = function(){
    var cv = el('maze-minimap');
    if (!cv || !this.grid) return;
    var ctx = cv.getContext('2d');
    var n = this.N, s = cv.width / n;
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = '#132029'; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = '#24413a';
    for (var j = 0; j < n; j++) for (var i = 0; i < n; i++) if (this.grid[j][i] === 1) ctx.fillRect(i * s, j * s, s, s);
    var dot = function(cell, color, sz){ ctx.fillStyle = color; ctx.fillRect(cell.i * s - sz / 2 + s / 2, cell.j * s - sz / 2 + s / 2, sz, sz); };
    dot({ i: this.exitCell[0], j: this.exitCell[1] }, this.state.monstersLeft === 0 && !this.boss.alive ? '#c6f24e' : '#5f7d8c', s * 1.2);
    for (var mi = 0; mi < this.monsters.length; mi++){ var m = this.monsters[mi]; if (m.alive) dot(this.worldToCell(m.x, m.z), m.kind === 'chase' ? '#d83a4a' : m.kind === 'patrol' ? '#8a56c8' : '#18a89c', s * 0.9); }
    if (this.boss.alive) dot(this.worldToCell(this.boss.x, this.boss.z), this.boss.awake ? '#ff7a2f' : '#4a5560', s * 1.4);
    dot(this.worldToCell(this.player.x, this.player.z), '#ffffff', s);
  };

  // ---- boot ----
  try {
    var game = new MazeGame({ difficulty: 'normal', mapSize: 'large', startSeconds: 210, maxLives: 3, levelCount: 3 });
    game.mount();
  } catch (e) {
    window.__errs.push('boot: ' + String(e));
    post({ type: 'load-error' });
  }
})();
</script>
</body>
</html>
`;
}
